/**
 * HealthStatusBanner — Banner fixo no topo que avisa quando o backend está lento ou fora.
 *
 * Só aparece quando o status é 'slow', 'degraded' ou 'down'.
 * Desaparece automaticamente quando o serviço volta ao normal.
 *
 * Usage:
 *   // No layout principal (App.tsx ou layout wrapper):
 *   <HealthStatusBanner />
 */
import { useHealthCheck, type HealthStatus } from '@/hooks/useHealthCheck';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ServerCrash, Gauge, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

const statusConfig: Record<
  Extract<HealthStatus, 'slow' | 'degraded' | 'down'>,
  {
    icon: typeof AlertTriangle;
    bgClass: string;
    textClass: string;
    labelKey: string;
    fallback: string;
  }
> = {
  slow: {
    icon: Gauge,
    bgClass: 'bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800',
    textClass: 'text-amber-800 dark:text-amber-200',
    labelKey: 'health.slow',
    fallback: 'O servidor está um pouco lento. Suas ações podem demorar mais que o normal.',
  },
  degraded: {
    icon: AlertTriangle,
    bgClass: 'bg-orange-50 dark:bg-orange-950/50 border-b border-orange-200 dark:border-orange-800',
    textClass: 'text-orange-800 dark:text-orange-200',
    labelKey: 'health.degraded',
    fallback: 'O servidor está com desempenho reduzido. Estamos trabalhando para normalizar.',
  },
  down: {
    icon: ServerCrash,
    bgClass: 'bg-destructive/10 border-b border-destructive/30',
    textClass: 'text-destructive',
    labelKey: 'health.down',
    fallback: 'O servidor está temporariamente indisponível. Tentando reconectar...',
  },
};

export function HealthStatusBanner() {
  const { t } = useTranslation();
  const { status, latencyMs, checkNow } = useHealthCheck();
  const [dismissed, setDismissed] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Reset dismissed when status changes
  useEffect(() => {
    if (status === 'healthy' || status === 'checking' || status === 'offline') {
      setDismissed(false);
    }
  }, [status]);

  // Don't show for healthy, checking, or offline (OfflineIndicator handles that)
  if (status === 'healthy' || status === 'checking' || status === 'offline') return null;
  if (dismissed) return null;

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  const handleRetry = async () => {
    setIsRetrying(true);
    await checkNow();
    setTimeout(() => setIsRetrying(false), 1000);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`fixed left-0 right-0 z-[190] px-4 py-2.5 ${config.bgClass}`}
      style={{ top: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="flex items-center justify-center gap-3 max-w-3xl mx-auto">
        <Icon className={`h-4 w-4 shrink-0 ${config.textClass}`} aria-hidden="true" />

        <p className={`text-sm font-medium ${config.textClass}`}>
          {t(config.labelKey, config.fallback)}
          {latencyMs !== null && status !== 'down' && (
            <span className="ml-1 text-xs opacity-70">({latencyMs}ms)</span>
          )}
        </p>

        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 px-2 text-xs ${config.textClass} hover:bg-transparent`}
            onClick={handleRetry}
            disabled={isRetrying}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
            {t('common.retry', 'Tentar novamente')}
          </Button>

          <button
            onClick={() => setDismissed(true)}
            className={`text-xs underline opacity-70 hover:opacity-100 ${config.textClass}`}
            aria-label={t('common.dismiss', 'Dispensar')}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
